import { create } from 'zustand';
import { client, setOnUnauthorized } from './client';
import { resetPrintConfigCache } from './printInvoice';
import {
  authApi,
  AuthApiError,
  type AuthBranch,
  type AuthDrawer,
  type AuthSubsidiary,
  type AuthUser,
  type SessionInfo,
} from './authApi';

/**
 * Multi-tenant auth state machine, mirroring the cashier flow:
 *   anonymous → subsidiary → branch → drawer → session
 * The final session JWT is a full cashier token accepted by every
 * /api/v1/appointments route. Persisted (token + session) under
 * `pos:appointments:mt-auth`; the api-client stores the token itself too.
 */

const STORAGE_KEY = 'pos:appointments:mt-auth';

export type AuthStage = 'anonymous' | 'subsidiary' | 'branch' | 'drawer' | 'session';

interface Persisted {
  token: string;
  expires_at: string;
  session: SessionInfo;
}

function decodeExpMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number };
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function loadPersisted(): Persisted | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Persisted;
    const exp = decodeExpMs(p.token);
    if (exp && exp < Date.now()) return null;
    return p;
  } catch {
    return null;
  }
}

function persist(p: Persisted): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

interface AuthState {
  stage: AuthStage;
  loading: boolean;
  /** True while adopting an SSO token from the cashier handoff (shows a splash). */
  adopting: boolean;
  /**
   * Machine error code, not display text — the store has no access to the
   * language context, so the login screen translates it at render time via
   * `authErrorMessage`. 'UNKNOWN' covers non-API failures.
   */
  error: string | null;

  pendingToken: string | null;
  user: AuthUser | null;
  subsidiaries: AuthSubsidiary[];
  subsidiary: AuthSubsidiary | null;
  branches: AuthBranch[];
  branch: AuthBranch | null;
  drawers: AuthDrawer[];

  token: string | null;
  session: SessionInfo | null;

  /**
   * Set when sign-in answered SETUP_REQUIRED: the tenant has not finished its
   * Initial Setup Wizard, so no session was issued. The credentials are held
   * in memory only, to sign in again the moment the wizard completes.
   */
  setup: { slug: string; username: string; password: string; tenantName: string; setupToken: string | null } | null;
  finishSetup: (owner?: { username: string; password: string }) => Promise<void>;
  exitSetup: () => void;

  init: () => void;
  adoptToken: (token: string) => Promise<void>;
  login: (tenantSlug: string, username: string, password: string) => Promise<void>;
  chooseSubsidiary: (id: number) => Promise<void>;
  chooseBranch: (id: number) => Promise<void>;
  chooseDrawer: (id: number) => Promise<void>;
  back: () => void;
  logout: () => void;
  clearError: () => void;
}

/** Reduce a thrown auth failure to the code the login screen will translate. */
function errorCode(err: unknown): string {
  return err instanceof AuthApiError ? err.code : 'UNKNOWN';
}

export const useAuthStore = create<AuthState>((set, get) => ({
  stage: 'anonymous',
  loading: false,
  adopting: false,
  error: null,
  pendingToken: null,
  user: null,
  subsidiaries: [],
  subsidiary: null,
  branches: [],
  branch: null,
  drawers: [],
  token: null,
  session: null,
  setup: null,

  finishSetup: async (owner) => {
    const s = get().setup;
    if (!s) return;
    set({ setup: null });
    await get().login(s.slug, owner?.username ?? s.username, owner?.password ?? s.password);
  },

  exitSetup: () => set({ setup: null, error: null }),

  init: () => {
    setOnUnauthorized(() => get().logout());
    const p = loadPersisted();
    if (p) {
      client.setToken(p.token);
      set({ stage: 'session', token: p.token, session: p.session });
    }
  },

  // Adopt a session JWT handed off from the cashier (SSO). Hydrates SessionInfo
  // via /me, persists, and lands straight in the calendar — no second login.
  adoptToken: async (token) => {
    setOnUnauthorized(() => get().logout());
    set({ adopting: true, error: null });
    try {
      const r = await authApi.me(token);
      client.setToken(token);
      const exp = decodeExpMs(token);
      const expires_at = exp ? new Date(exp).toISOString() : new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
      persist({ token, expires_at, session: r.session });
      set({ stage: 'session', token, session: r.session, adopting: false });
    } catch {
      // Bad/expired handoff token — fall back to the normal login screen.
      set({ adopting: false, stage: 'anonymous' });
    }
  },

  login: async (tenantSlug, username, password) => {
    set({ loading: true, error: null });
    try {
      const r = await authApi.login(tenantSlug.trim(), username.trim(), password);
      // Auto-advance when there's exactly one subsidiary.
      set({ pendingToken: r.pending_token, user: r.user, subsidiaries: r.available_subsidiaries, stage: 'subsidiary', loading: false });
      if (r.available_subsidiaries.length === 1) await get().chooseSubsidiary(r.available_subsidiaries[0]!.id);
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'SETUP_REQUIRED') {
        const b = err.body ?? {};
        set({
          loading: false,
          error: null,
          setup: {
            slug: tenantSlug.trim(),
            username: username.trim(),
            password,
            tenantName: typeof b.tenant_name === 'string' ? b.tenant_name : tenantSlug.trim(),
            setupToken: typeof b.setup_token === 'string' ? b.setup_token : null,
          },
        });
        return;
      }
      set({ loading: false, error: errorCode(err) });
    }
  },

  chooseSubsidiary: async (id) => {
    const token = get().pendingToken;
    if (!token) return;
    set({ loading: true, error: null });
    try {
      const r = await authApi.selectSubsidiary(id, token);
      set({ pendingToken: r.pending_token, subsidiary: r.subsidiary, branches: r.available_branches, stage: 'branch', loading: false });
      if (r.available_branches.length === 1) await get().chooseBranch(r.available_branches[0]!.id);
    } catch (err) {
      set({ loading: false, error: errorCode(err) });
    }
  },

  chooseBranch: async (id) => {
    const token = get().pendingToken;
    if (!token) return;
    set({ loading: true, error: null });
    try {
      const r = await authApi.selectBranch(id, token);
      set({ pendingToken: r.pending_token, branch: r.branch, drawers: r.available_cash_drawers, stage: 'drawer', loading: false });
      if (r.available_cash_drawers.length === 1) await get().chooseDrawer(r.available_cash_drawers[0]!.id);
    } catch (err) {
      set({ loading: false, error: errorCode(err) });
    }
  },

  chooseDrawer: async (id) => {
    const token = get().pendingToken;
    if (!token) return;
    set({ loading: true, error: null });
    try {
      const r = await authApi.selectDrawer(id, token);
      client.setToken(r.token);
      persist({ token: r.token, expires_at: r.expires_at, session: r.session });
      set({ stage: 'session', token: r.token, session: r.session, pendingToken: null, loading: false });
    } catch (err) {
      set({ loading: false, error: errorCode(err) });
    }
  },

  back: () => {
    const { stage } = get();
    if (stage === 'subsidiary') set({ stage: 'anonymous', pendingToken: null, subsidiaries: [] });
    else if (stage === 'branch') set({ stage: 'subsidiary', branches: [], subsidiary: null });
    else if (stage === 'drawer') set({ stage: 'branch', drawers: [], branch: null });
  },

  logout: () => {
    client.clearToken();
    resetPrintConfigCache();
    localStorage.removeItem(STORAGE_KEY);
    set({
      stage: 'anonymous', loading: false, error: null, pendingToken: null, user: null,
      subsidiaries: [], subsidiary: null, branches: [], branch: null, drawers: [], token: null, session: null,
    });
  },

  clearError: () => set({ error: null }),
}));
